import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import HorizonRing from "@/components/HorizonRing";
import LevelUpModal from "@/components/LevelUpModal";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import ShareButton from "@/components/ShareButton";
import CareerPathCard from "@/components/CareerPathCard";
import { useAuth } from "@/context/AuthContext";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Flame,
    Eye,
    HandHeart,
    ArrowLeft,
    Users,
    Hammer,
    Compass,
    Wrench,
    ListChecks,
    Sparkles,
    BookOpen,
    Route,
    Trophy,
    AlertTriangle,
    Check,
    X,
    Info,
    ChevronDown,
    ChevronUp,
    Briefcase,
    Building2,
} from "lucide-react";

export default function Discipline() {
    const { slug } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [detail, setDetail] = useState(null);
    const [roster, setRoster] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [microtasks, setMicrotasks] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [mineMode, setMineMode] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reward, setReward] = useState(null);

    // Roadmap
    const [roadmap, setRoadmap] = useState(null);
    const [roadmapLoading, setRoadmapLoading] = useState(false);
    const [roadmapError, setRoadmapError] = useState(null);
    const [roadmapOpen, setRoadmapOpen] = useState(false);

    // Empty state dialog
    const [showEmptyDialog, setShowEmptyDialog] = useState(false);
    const [emptyDialogDismissed, setEmptyDialogDismissed] = useState(false);

    const load = () => {
        // Public — everyone can read these
        api.get(`/atlas/discipline/${slug}`).then((r) => setDetail(r.data)).catch(() => {});
        api.get(`/beacons/discipline/${slug}`).then((r) => setRoster(r.data)).catch(() => {});
        api.get(`/workshop/${slug}/ledger?limit=8`).then((r) => setLedger(r.data)).catch(() => {});
        api.get(`/jobs?discipline_slug=${slug}&limit=6`).then((r) => setJobs(r.data || [])).catch(() => {});
        // Auth-only — skip cleanly for guests
        if (user) {
            api.get(`/microtasks/discipline/${slug}`).then((r) => setMicrotasks(r.data.microtasks || [])).catch(() => {});
            api.get("/beacons/me").then((r) => {
                const mine = r.data.find((b) => b.discipline_slug === slug);
                setMineMode(mine ? mine.mode : null);
            }).catch(() => {});
        } else {
            setMicrotasks([]);
            setMineMode(null);
        }
    };

    useEffect(() => {
        load();
        setRoadmap(null);
        setRoadmapError(null);
        setEmptyDialogDismissed(false);
    }, [slug, user?.id]);

    // Trigger empty-discipline dialog once (per discipline visit) if 0 contributors — logged-in users only
    useEffect(() => {
        if (!user) return;
        if (!detail || emptyDialogDismissed) return;
        if ((detail.contributor_count || 0) === 0) {
            const key = `atlas-empty-dialog-${slug}`;
            if (!sessionStorage.getItem(key)) {
                setShowEmptyDialog(true);
                sessionStorage.setItem(key, "1");
            }
        }
    }, [detail, slug, emptyDialogDismissed, user]);

    // SEO: update document title + meta description when discipline loads
    useEffect(() => {
        if (!detail) return;
        const desc = detail.summary || `${detail.name} — join contributors and observers on Project Atlas.`;
        document.title = `${detail.name} · Project Atlas`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "description");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", String(desc).slice(0, 155));
    }, [detail]);

    const loadRoadmap = async () => {
        if (roadmap || roadmapLoading) return;
        setRoadmapLoading(true);
        setRoadmapError(null);
        try {
            const { data } = await api.get(`/disciplines/${slug}/roadmap`);
            setRoadmap(data);
        } catch (e) {
            setRoadmapError(e?.response?.data?.detail || "Couldn't load roadmap.");
        } finally {
            setRoadmapLoading(false);
        }
    };

    const ignite = async (mode) => {
        setBusy(true);
        try {
            const { data } = await api.post("/beacons", { discipline_slug: slug, mode });
            setReward(data.reward);
            load();
        } finally {
            setBusy(false);
        }
    };

    const completeMicrotask = async (task) => {
        setBusy(true);
        try {
            const { data } = await api.post("/microtasks/complete", {
                microtask_id: task.id,
            });
            if (data.reward) setReward(data.reward);
            toast.success("Microtask logged.");
            load();
        } catch {
            toast.error("Couldn't complete that task.");
        } finally {
            setBusy(false);
        }
    };

    if (!detail)
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">
                Loading discipline…
            </div>
        );

    const meta = BRANCH_META[detail.branch];
    const isEmpty = (detail.contributor_count || 0) === 0;
    const pendingMicrotasks = microtasks.filter((t) => !t.completed);

    return (
        <div className="min-h-screen w-full pt-24 pb-16 px-4 sm:px-8">
            <TopBar />

            {!user && (
                <div className="max-w-6xl mx-auto mb-4">
                    <div
                        className="crystal rounded-3xl p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
                        style={{ background: `linear-gradient(90deg, ${BRANCH_META[detail.branch].soft}, #ffffff)` }}
                        data-testid="guest-cta-banner"
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <Compass className="w-5 h-5 flex-shrink-0" color={BRANCH_META[detail.branch].color} />
                            <div className="min-w-0">
                                <div className="font-display font-black tracking-tight text-slate-900">
                                    You're viewing {detail.name} as a guest.
                                </div>
                                <div className="text-sm text-slate-600">
                                    Sign up (free first month) to ignite a beacon, take microtasks, and post projects here.
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <ShareButton path={`/discipline/${slug}`} title={detail.name} variant="ghost" label="Share" />
                            <Link
                                to="/"
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="guest-signup-btn"
                            >
                                Sign up free <ArrowLeft className="w-4 h-4 rotate-180" />
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                <Link
                    to={user ? "/atlas" : "/"}
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
                    data-testid="back-to-atlas"
                >
                    <ArrowLeft className="w-4 h-4" /> {user ? "Back to the map" : "Back to home"}
                </Link>

                <div className="mt-4 grid md:grid-cols-3 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="md:col-span-2 crystal rounded-3xl p-6 sm:p-8"
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                                {meta.name}
                            </span>
                            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">
                                {detail.roster_count} on roster · {detail.contributor_count} contributors
                            </span>
                            {isEmpty && (
                                <button
                                    onClick={() => setShowEmptyDialog(true)}
                                    className="chip"
                                    style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text, cursor: "pointer" }}
                                    data-testid="empty-discipline-badge"
                                >
                                    <Info className="w-3 h-3" /> Frontier discipline
                                </button>
                            )}
                        </div>

                        <h1
                            className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-slate-900 mt-3 leading-none"
                            data-testid="discipline-title"
                        >
                            {detail.name}
                        </h1>
                        <p className="text-slate-600 text-lg mt-3 max-w-2xl">{detail.blurb}</p>

                        <div className="mt-8 flex items-center gap-5">
                            <HorizonRing size={110} stroke={12} pct={detail.progress} color={meta.color} label="mapped" />
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-400">
                                    Community frontier
                                </div>
                                <p className="text-slate-700 mt-1 max-w-md">
                                    {detail.progress < 40
                                        ? "Wide-open. Beginner tasks abundant, plenty of first-authorship left."
                                        : detail.progress < 70
                                          ? "Well-formed community. Solid workshops and steady mentorship."
                                          : "Mature discipline. Contributions land inside a well-lit landscape."}
                                </p>
                                <div className="mt-2 text-sm font-bold text-slate-900">
                                    {detail.open_tasks} open micro-tasks · {pendingMicrotasks.length} you can do now
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 border-t border-slate-200/70 pt-6">
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">
                                Ignite your beacon
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    disabled={busy}
                                    onClick={() => ignite("observer")}
                                    className="arcade-btn"
                                    data-variant={mineMode === "observer" ? detail.branch : "ghost"}
                                    data-testid="beacon-observer"
                                >
                                    <Eye className="w-4 h-4" /> Observer
                                </button>
                                <button
                                    disabled={busy}
                                    onClick={() => ignite("contributor")}
                                    className="arcade-btn"
                                    data-variant={mineMode === "contributor" ? detail.branch : "ghost"}
                                    data-testid="beacon-contributor"
                                >
                                    <HandHeart className="w-4 h-4" /> Contributor
                                </button>
                                <button
                                    onClick={() => nav(`/workshop/${slug}`)}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="enter-workshop"
                                >
                                    <Wrench className="w-4 h-4" /> Enter workshop
                                </button>
                                <button
                                    onClick={() => nav(`/horizon/${slug}`)}
                                    className="arcade-btn"
                                    data-testid="view-horizon"
                                >
                                    <Compass className="w-4 h-4" /> View horizon
                                </button>
                            </div>
                            {mineMode && (
                                <div className="text-xs mt-3 font-display font-bold" style={{ color: meta.color }} data-testid="current-mode">
                                    Your current mode: {mineMode}
                                </div>
                            )}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="crystal rounded-3xl p-6"
                    >
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-400">
                            <Users className="w-3 h-3" /> Live roster
                        </div>
                        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto no-scrollbar">
                            {roster.length === 0 && (
                                <p className="text-sm text-slate-500">
                                    Be the first Explorer to light this beacon.
                                </p>
                            )}
                            {roster.map((b, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                    <div
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            background: b.mode === "contributor" ? meta.color : "#8BCDF8",
                                            boxShadow: b.mode === "contributor" ? `0 0 6px ${meta.color}` : "none",
                                        }}
                                    />
                                    <span className="font-display font-bold text-slate-900 text-sm">{b.user_name}</span>
                                    <span className="ml-auto text-xs text-slate-400 uppercase font-bold tracking-widest">{b.mode}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Career Path Guide — exemplar organization + roadmap */}
                <div className="mt-8" data-testid="career-path-section">
                    <CareerPathCard disciplineSlug={slug} />
                </div>

                {/* Microtasks strip */}
                {microtasks.length > 0 && (
                    <div className="mt-8 crystal rounded-3xl p-6" data-testid="discipline-microtasks">
                        <div className="flex items-start gap-3 flex-wrap">
                            <div className="w-11 h-11 rounded-2xl bg-slate-900 text-cream flex items-center justify-center flex-shrink-0">
                                <ListChecks className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="font-display font-bold text-2xl tracking-tight text-slate-900">
                                    Microtasks you can complete now
                                </h2>
                                <p className="text-sm text-slate-600 mt-0.5">
                                    Small, high-signal actions that earn XP and appear on your ledger. No approval needed.
                                </p>
                            </div>
                            <Link to="/interests" className="arcade-btn" data-variant="ghost" data-testid="all-microtasks-link">
                                All in dashboard →
                            </Link>
                        </div>
                        <ul className="mt-4 grid md:grid-cols-2 gap-3">
                            {microtasks.slice(0, 4).map((t) => (
                                <li key={t.id} className={`rounded-2xl p-4 flex items-start gap-3 ${t.completed ? "bg-slate-50 border border-slate-200" : "bg-white/80 border border-slate-200"}`} data-testid={`microtask-${t.id}`}>
                                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white" style={{ background: t.completed ? meta.color : "rgba(148,163,184,0.35)" }}>
                                        {t.completed ? <Check className="w-3.5 h-3.5" /> : <Flame className="w-3.5 h-3.5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`font-display font-bold text-slate-900 ${t.completed ? "line-through text-slate-400" : ""}`}>
                                                {t.title}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                                +{t.xp} XP
                                            </span>
                                        </div>
                                        <p className={`text-xs mt-1 ${t.completed ? "text-slate-400" : "text-slate-600"}`}>{t.description}</p>
                                    </div>
                                    {!t.completed && (
                                        <button
                                            onClick={() => completeMicrotask(t)}
                                            disabled={busy}
                                            className="arcade-btn flex-shrink-0"
                                            data-variant={detail.branch}
                                            data-testid={`quick-complete-${t.id}`}
                                            style={{ padding: "0.35rem 0.75rem", fontSize: "0.7rem" }}
                                        >
                                            <Check className="w-3 h-3" /> Do
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Roadmap section */}
                <div className="mt-8 crystal rounded-3xl p-6" data-testid="discipline-roadmap">
                    <button
                        onClick={() => {
                            if (!roadmap && !roadmapError) loadRoadmap();
                            setRoadmapOpen((v) => !v);
                        }}
                        className="w-full flex items-start gap-3 text-left"
                        data-testid="roadmap-toggle"
                    >
                        <div className="w-11 h-11 rounded-2xl flex-shrink-0 flex items-center justify-center" style={{ background: meta.soft, color: meta.text }}>
                            <Route className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-display font-bold text-2xl tracking-tight text-slate-900">
                                Skills & Roadmap
                            </h2>
                            <p className="text-sm text-slate-600 mt-0.5">
                                A concrete path — required skills, learning stages with real resources, common pitfalls.
                            </p>
                        </div>
                        <div className="flex-shrink-0 mt-1 text-slate-500">
                            {roadmapOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                    </button>

                    {roadmapOpen && (
                        <div className="mt-5" data-testid="roadmap-body">
                            {!roadmap && !roadmapLoading && !roadmapError && (
                                <button onClick={loadRoadmap} className="arcade-btn" data-variant={detail.branch} data-testid="load-roadmap">
                                    <Sparkles className="w-4 h-4" /> Generate roadmap
                                </button>
                            )}
                            {roadmapLoading && (
                                <div className="text-sm text-slate-500 flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                                    Assembling roadmap from Claude…
                                </div>
                            )}
                            {roadmapError && (
                                <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900">
                                    <div className="font-display font-bold flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4" /> {roadmapError}</div>
                                    <button onClick={loadRoadmap} className="arcade-btn mt-2" data-variant="ghost" style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem" }}>
                                        Retry
                                    </button>
                                </div>
                            )}
                            {roadmap && (
                                <div className="space-y-6">
                                    <section>
                                        <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
                                            <BookOpen className="w-3 h-3" /> Overview
                                        </h3>
                                        <p className="text-slate-800 leading-relaxed">{roadmap.overview}</p>
                                        {roadmap.why_it_matters && (
                                            <p className="text-slate-600 mt-2 italic leading-relaxed">{roadmap.why_it_matters}</p>
                                        )}
                                    </section>

                                    {roadmap.required_skills?.length > 0 && (
                                        <section>
                                            <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">
                                                Required skills
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                {roadmap.required_skills.map((s, i) => (
                                                    <span key={i} className="chip" style={{ background: meta.soft, color: meta.text }} data-testid={`skill-${i}`}>
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        </section>
                                    )}

                                    {roadmap.learning_stages?.length > 0 && (
                                        <section>
                                            <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">
                                                Learning stages
                                            </h3>
                                            <div className="space-y-4">
                                                {roadmap.learning_stages.map((st, i) => (
                                                    <div key={i} className="rounded-2xl border border-slate-200 bg-white/70 p-4" data-testid={`stage-${i}`}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-6 h-6 rounded-full flex items-center justify-center text-white font-display font-bold text-xs" style={{ background: meta.color }}>
                                                                {i + 1}
                                                            </span>
                                                            <div className="font-display font-bold text-slate-900">{st.stage}</div>
                                                        </div>
                                                        <p className="text-sm text-slate-700 mt-2">{st.focus}</p>
                                                        {st.resources?.length > 0 && (
                                                            <ul className="mt-2 space-y-1 text-sm text-slate-600">
                                                                {st.resources.map((r, ri) => (
                                                                    <li key={ri} className="flex items-start gap-2">
                                                                        <span className="text-slate-400 mt-0.5">·</span>
                                                                        <span>{r}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    )}

                                    {roadmap.success_signals?.length > 0 && (
                                        <section>
                                            <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 flex items-center gap-1.5">
                                                <Trophy className="w-3 h-3" /> Success signals
                                            </h3>
                                            <ul className="space-y-1 text-sm text-slate-800">
                                                {roadmap.success_signals.map((s, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
                                                        <span>{s}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}

                                    {roadmap.common_pitfalls?.length > 0 && (
                                        <section>
                                            <h3 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 flex items-center gap-1.5">
                                                <AlertTriangle className="w-3 h-3" /> Common pitfalls
                                            </h3>
                                            <ul className="space-y-1 text-sm text-slate-700">
                                                {roadmap.common_pitfalls.map((p, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <span className="text-amber-500 mt-0.5">!</span>
                                                        <span>{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {detail.projects?.length > 0 && (
                    <div className="mt-8">
                        <h2 className="font-display font-bold text-2xl tracking-tight text-slate-900">
                            Active projects
                        </h2>
                        <div className="mt-4 grid md:grid-cols-2 gap-4">
                            {detail.projects.map((p) => (
                                <div key={p.slug} className="crystal rounded-2xl p-5">
                                    <div className="flex items-center gap-2">
                                        <Hammer className="w-4 h-4" color={meta.color} />
                                        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: meta.text }}>
                                            {p.org}
                                        </div>
                                    </div>
                                    <h3 className="font-display font-bold text-lg text-slate-900 mt-2">
                                        {p.title}
                                    </h3>
                                    <p className="text-sm text-slate-600 mt-1">{p.summary}</p>
                                    <div className="text-xs mt-3 text-slate-500">
                                        Lead: <span className="font-bold text-slate-800">{p.lead_name}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Companies hiring in this discipline */}
                {jobs.length > 0 && (
                    <div className="mt-8" data-testid="discipline-jobs-section">
                        <div className="flex items-baseline justify-between">
                            <h2 className="font-display font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
                                <Briefcase className="w-5 h-5" color={meta.color} /> Companies hiring here
                            </h2>
                            <Link to="/careers" className="text-sm font-bold text-humanities" data-testid="all-careers-link">
                                See all careers →
                            </Link>
                        </div>
                        <div className="mt-4 grid md:grid-cols-2 gap-4">
                            {jobs.map((j) => (
                                <Link
                                    key={j.id}
                                    to={`/jobs/${j.id}`}
                                    className="crystal rounded-2xl p-5 hover:shadow-xl transition-all group"
                                    data-testid={`discipline-job-${j.id}`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Building2 className="w-4 h-4" color={meta.color} />
                                        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: meta.text }}>
                                            {j.org_name || j.posted_by_name}
                                        </div>
                                        {j.org_verified && <VerifiedOrgBadge />}
                                    </div>
                                    <h3 className="font-display font-bold text-lg text-slate-900 mt-2 group-hover:text-humanities transition-colors">
                                        {j.title}
                                    </h3>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {j.location || "Remote"}
                                        {j.compensation_note && <> · <span className="font-mono">{j.compensation_note}</span></>}
                                    </div>
                                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{j.description}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {ledger.length > 0 && (
                    <div className="mt-8 crystal rounded-3xl p-6">
                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Recent activity
                        </h2>
                        <div className="mt-3 space-y-2">
                            {ledger.map((e, i) => (
                                <div key={i} className="ledger-row text-slate-600">
                                    <span style={{ color: meta.color }}>
                                        {String(e.event_type).padEnd(8, " ")}
                                    </span>{" "}
                                    <span className="text-slate-400">
                                        {new Date(e.created_at).toLocaleString(undefined, {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>{" "}
                                    <span className="text-slate-800">{e.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Empty-discipline dialog */}
            <AnimatePresence>
                {showEmptyDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4"
                        onClick={() => { setShowEmptyDialog(false); setEmptyDialogDismissed(true); }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="crystal rounded-3xl p-7 w-full max-w-md relative"
                            data-testid="empty-discipline-dialog"
                        >
                            <button
                                type="button"
                                onClick={() => { setShowEmptyDialog(false); setEmptyDialogDismissed(true); }}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"
                                data-testid="empty-dialog-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: meta.soft }}>
                                <Compass className="w-7 h-7" style={{ color: meta.color }} />
                            </div>
                            <p className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                                <Info className="w-3 h-3" /> Frontier discipline
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                No contributors here yet.
                            </h2>
                            <p className="text-slate-700 mt-2 leading-relaxed">
                                <strong>{detail.name}</strong> is a wide-open frontier — no one has claimed a project
                                or posted work yet. That's not a dead end, it's an opening.
                            </p>
                            <ul className="mt-3 space-y-2 text-sm text-slate-700">
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: meta.color }} />
                                    <span>Complete a <strong>microtask</strong> below to plant your flag and earn XP.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: meta.color }} />
                                    <span>Ignite your <strong>beacon</strong> so future contributors can find you.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: meta.color }} />
                                    <span>Once contributors post projects, more tasks will appear here automatically.</span>
                                </li>
                            </ul>
                            <div className="mt-5 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setShowEmptyDialog(false); setEmptyDialogDismissed(true); }}
                                    className="arcade-btn"
                                    data-variant="ghost"
                                    data-testid="empty-dialog-dismiss"
                                >
                                    Got it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowEmptyDialog(false); setEmptyDialogDismissed(true); ignite("contributor"); }}
                                    className="arcade-btn"
                                    data-variant={detail.branch}
                                    data-testid="empty-dialog-ignite"
                                >
                                    <Flame className="w-4 h-4" /> Ignite as contributor
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <LevelUpModal reward={reward} onClose={() => setReward(null)} />
        </div>
    );
}
